// Move a piano's complete staves into two independently editable parts.
// This is a structural edit: concert pitches and written music do not change.

import { midiOf } from './pitch';
import { isPartMeasurePlaybackRule } from './playbackRules';
import type { NoteEvent, Part, Score, Voice, VoiceItem } from './types';

export type PartStaffSplitCapability =
  | { canSplit: true }
  | { canSplit: false; reason: string };

function eventsOf(items: readonly VoiceItem[]): NoteEvent[] {
  return items.flatMap((item) => item.kind === 'tuplet' ? eventsOf(item.items) : [item]);
}

function staffOf(voice: Voice): number {
  const first = eventsOf(voice.items)[0];
  return first?.staff ?? voice.staff ?? 0;
}

/** Mixed-staff voices need a rhythmic rewrite, and a tie crossing into the
 * other resulting part would lose its shared instrument. Decline those cases
 * explicitly rather than silently dropping notes or breaking their timing. */
export function getPartStaffSplitCapability(part: Part | undefined): PartStaffSplitCapability {
  if (!part) return { canSplit: false, reason: 'Choose a part to split.' };
  if (part.staves.length !== 2 || part.staves[0].clef !== 'treble' || part.staves[1].clef !== 'bass') {
    return { canSplit: false, reason: 'Choose a part with a treble staff above a bass staff.' };
  }
  const openTies = new Map<string, number>();
  for (const measure of part.measures) {
    if (measure.playback?.some((rule) => !isPartMeasurePlaybackRule(rule) ||
      (rule.staff !== undefined && rule.staff !== 0 && rule.staff !== 1))) {
      return { canSplit: false, reason: 'Correct this part’s playback rules before splitting its staves.' };
    }
    for (const [voiceIndex, voice] of measure.voices.entries()) {
      const home = voice.staff ?? 0;
      const events = eventsOf(voice.items);
      if ((home !== 0 && home !== 1) || events.some((event) =>
        event.staff !== undefined && event.staff !== 0 && event.staff !== 1)) {
        return { canSplit: false, reason: 'Correct this part’s staff assignments before splitting its staves.' };
      }
      const target = staffOf(voice);
      if (events.some((event) => (event.staff ?? home) !== target)) {
        return { canSplit: false, reason: 'A voice moves between staves. Keep it in one part to preserve its rhythm.' };
      }
      for (const event of events) {
        for (const head of event.notes) {
          const key = `${voiceIndex}:${midiOf(head.pitch)}`;
          const previousStaff = openTies.get(key);
          if ((head.tie === 'stop' || head.tie === 'both') &&
            previousStaff !== undefined && previousStaff !== target) {
            return { canSplit: false, reason: 'A tie joins the two staves. Keep them in one part to preserve the held note.' };
          }
          if (head.tie === 'start' || head.tie === 'both') openTies.set(key, target);
          else openTies.delete(key);
        }
      }
    }
  }
  return { canSplit: true };
}

function unusedId(base: string, used: Set<string>): string {
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) candidate = `${base}:${suffix++}`;
  used.add(candidate);
  return candidate;
}

function unusedName(base: string, parts: readonly Part[]): string {
  const names = new Set(parts.map(({ name }) => name));
  let candidate = base;
  let suffix = 2;
  while (names.has(candidate)) candidate = `${base} ${suffix++}`;
  return candidate;
}

function onSingleStaff(items: readonly VoiceItem[]): VoiceItem[] {
  return items.map((item) => {
    if (item.kind === 'tuplet') return { ...item, items: onSingleStaff(item.items) };
    return item.staff === undefined || item.staff === 0 ? item : { ...item, staff: 0 };
  });
}

/** Replace a two-staff part in place with Lead and Bass. All the treble voices
 * go to Lead; all the bass voices go to Bass. Keep source IDs on the Lead and
 * moving voices/events, so selections and event-anchored spanners survive.
 * Both parts retain the source instrument so a structural split does not
 * replace polyphonic piano audition with monophonic lead/bass instruments.
 * The Bass gets no new octave transposition: both parts inherit exactly the
 * source part's written/concert relationship. Unsupported edits are no-ops;
 * getPartStaffSplitCapability supplies the reason for the editor. */
export function splitPartStaves(score: Score, partId: string): Score {
  const partIndex = score.parts.findIndex(({ id }) => id === partId);
  const source = score.parts[partIndex];
  if (!getPartStaffSplitCapability(source).canSplit || source.measures.length !== score.form.length) return score;

  const otherParts = score.parts.filter((_, index) => index !== partIndex);
  const partIds = new Set(score.parts.map(({ id }) => id));
  const measureIds = new Set(score.parts.flatMap(({ measures }) => measures.map(({ id }) => id)));
  const split = ([0, 1] as const).map((staff): Part => {
    const name = staff === 0 ? 'Lead' : 'Bass';
    return {
      ...source,
      id: staff === 0 ? source.id : unusedId(`${source.id}:bass`, partIds),
      name: unusedName(name, otherParts),
      shortName: name,
      instrument: source.instrument ?? 'piano',
      role: staff === 0 ? 'lead' : 'bass',
      staves: [source.staves[staff]],
      measures: source.measures.map((measure) => ({
        ...measure,
        id: staff === 0 ? measure.id : unusedId(`${measure.id}:bass`, measureIds),
        voices: measure.voices.filter((voice) => staffOf(voice) === staff).map((voice) => ({
          ...voice,
          ...(voice.staff === undefined ? {} : { staff: 0 }),
          items: onSingleStaff(voice.items),
        })),
        ...(measure.playback === undefined ? {} : {
          playback: measure.playback.filter((rule) => rule.staff === undefined || rule.staff === staff)
            .map((rule) => rule.staff === undefined ? rule : { ...rule, staff: 0 }),
        }),
      })),
    };
  });
  return { ...score, parts: [
    ...score.parts.slice(0, partIndex), ...split, ...score.parts.slice(partIndex + 1),
  ] };
}
